import { useState } from 'react'
import moment from 'moment-timezone'

export enum WeekSubMode {
    parite = 'parite',
    cycle = 'cycle'
}

export enum PariteType {
    paire = 'paire',
    impaire = 'impaire'
}

export const MAX_CYCLE_SIZE: number = 8

interface WeekPatternMutationVariables {
    weekCycle: number | undefined
    weekOffsets: number[] | undefined
    referenceDate: string | undefined
}

export interface UseWeekPatternReturn {
    chooseWeekPattern: boolean
    setChooseWeekPattern: (value: boolean) => void
    weekSubMode: WeekSubMode
    setWeekSubMode: (mode: WeekSubMode) => void
    pariteType: PariteType
    setPariteType: (type: PariteType) => void
    weekCycle: number
    weekOffsets: number[]
    handleCycleSizeChange: (size: number) => void
    handleWeekBoxClick: (index: number) => void
    getMutationVariables: (startDate: string) => WeekPatternMutationVariables
}

export function useWeekPattern(): UseWeekPatternReturn {
    const [chooseWeekPattern, setChooseWeekPattern] = useState<boolean>(false)
    const [weekSubMode, setWeekSubMode] = useState<WeekSubMode>(WeekSubMode.cycle)
    const [pariteType, setPariteType] = useState<PariteType>(PariteType.paire)
    const [weekCycle, setWeekCycle] = useState<number>(2)
    const [weekOffsets, setWeekOffsets] = useState<number[]>([0])

    function handleCycleSizeChange(size: number): void {
        if (size > MAX_CYCLE_SIZE) return
        setWeekCycle(size)
        const validOffsets: number[] = weekOffsets.filter((o: number): boolean => o < size)
        setWeekOffsets(validOffsets.length > 0 ? validOffsets : [0])
    }

    function handleWeekBoxClick(index: number): void {
        const isActive: boolean = weekOffsets.includes(index)
        if (isActive) {
            if (weekOffsets.length > 1) {
                setWeekOffsets(weekOffsets.filter((o: number): boolean => o !== index))
            }
        } else {
            setWeekOffsets([...weekOffsets, index].sort((a: number, b: number): number => a - b))
        }
    }

    function computeReferenceDate(startDate: string): string {
        if (weekSubMode === WeekSubMode.parite) {
            return pariteType === PariteType.paire
                ? moment().month(0).date(11).startOf('isoWeek').format('DD-MM-YYYY')
                : moment().month(0).date(4).startOf('isoWeek').format('DD-MM-YYYY')
        }
        return startDate
    }

    function getMutationVariables(startDate: string): WeekPatternMutationVariables {
        if (!chooseWeekPattern) {
            return { weekCycle: undefined, weekOffsets: undefined, referenceDate: undefined }
        }
        return {
            weekCycle: weekSubMode === WeekSubMode.cycle ? weekCycle : 2,
            weekOffsets: weekSubMode === WeekSubMode.cycle ? weekOffsets : [0],
            referenceDate: computeReferenceDate(startDate)
        }
    }

    return {
        chooseWeekPattern,
        setChooseWeekPattern,
        weekSubMode,
        setWeekSubMode,
        pariteType,
        setPariteType,
        weekCycle,
        weekOffsets,
        handleCycleSizeChange,
        handleWeekBoxClick,
        getMutationVariables
    }
}
